<template>
  <v-card>
    <v-navigation-drawer
      :rail="rail"
      location="right"
      width="320"
      @click="rail = false"
    >
      <v-list-item title="SETTINGS">
        <template #prepend>
          <v-btn
            variant="text"
            :icon="navigateIcon"
            @click.stop="rail = !rail"
          ></v-btn>
        </template>
      </v-list-item>

      <v-divider />

      <v-tabs v-model="tab" align-tabs="title">
        <v-tab prepend-icon="show_chart" value="chart">Chart</v-tab>
        <v-tab prepend-icon="settings" value="quick">Quick</v-tab>
      </v-tabs>

      <v-card-text>
        <v-window v-model="tab">
          <v-window-item value="chart">
            <v-list v-if="!rail" nav class="pl-5 ml-5 py-0">
              <div class="d-flex justify-start">
                <div class="chart-action-btn rounded-lg">
                  <v-btn
                    color="primary"
                    stacked
                    text="Pause"
                    variant="plain"
                    :disabled="disabled || isDemoMode || !isPollingMode"
                    @click="isPollingMode = false"
                  >
                    <template #prepend>
                      <v-icon size="36">pause</v-icon>
                    </template>
                  </v-btn>
                </div>

                <div class="chart-action-btn rounded-lg">
                  <v-btn
                    color="primary"
                    stacked
                    text="Start"
                    variant="plain"
                    :disabled="disabled || isDemoMode || isPollingMode"
                    @click="isPollingMode = true"
                  >
                    <template #prepend>
                      <v-icon size="36">play_arrow</v-icon>
                    </template>
                  </v-btn>
                </div>
              </div>
            </v-list>
            <v-list v-if="!rail" nav class="pl-5 ml-5">
              <v-switch
                v-model="isDemoMode"
                label="Demo Mode"
                :disabled="isPollingMode"
                hide-details
                color="primary"
              />
            </v-list>
            <v-list v-if="!rail" nav class="pl-5 ml-5 pr-5 mr-5">
              <v-select
                v-model="rows"
                :items="items"
                label="Rows"
                variant="underlined"
              />
            </v-list>
            <v-list v-if="!rail" nav class="pl-5 ml-5 pr-5 mr-5">
              <v-select
                v-model="cols"
                :items="items"
                label="Columns"
                variant="underlined"
              />
            </v-list>
            <v-list v-if="!rail" nav class="pl-5 ml-5 pr-5 mr-5">
              <v-text-field
                v-model="timespan"
                label="Visible History (s)"
                type="number"
                variant="underlined"
                :error="timespanError"
                :error-messages="timespanErrorMsg"
                @keydown="onKeyDown"
              />
            </v-list>
            <v-list v-if="!rail" nav class="pl-5 ml-5 pr-5 mr-5">
              <v-text-field
                v-model="intervalTimeMs"
                label="Refresh Interval (ms)"
                type="number"
                variant="underlined"
                :error="intervalTimeMsError"
                :error-messages="intervalTimeMsErrorMsg"
                @keydown="onKeyDown"
              />
            </v-list>
          </v-window-item>
          <v-window-item value="quick">
            <QuickSettingsCard :rail="rail"></QuickSettingsCard>
          </v-window-item>
        </v-window>
      </v-card-text>
    </v-navigation-drawer>
    <v-main class="pa-10">
      <v-row v-for="r in rows" :key="'chart-page-row-' + r" no-gutters>
        <v-col
          v-for="c in cols"
          :key="`chart-page-col-${r}x${c}-${Date.now()}`"
          cols="12"
          :xl="chartCol"
          :lg="chartCol"
          :md="chartCol"
          :sm="chartCol"
          :xs="chartCol"
        >
          <PollingDataGraph class="pa-2" :chartid="`${r}-${c}`" />
        </v-col>
      </v-row>
    </v-main>
  </v-card>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import PollingDataGraph from "@/components/PollingDataGraph.vue";
import QuickSettingsCard from "@/components/QuickSettingsCard.vue";
import { useSensorNames } from "@/composables/useSensorNames";
import { useSensorUnits } from "@/composables/useSensorUnits";
import { useStorage } from "@/composables/useStorage";
import { AccessLevel } from "@/enums/AccessLevel";
import { PortState } from "@/enums/PortState";
import { useModbusStore } from "@/stores/useModbusStore";
import { TimerId } from "@/types/TimerId";

const modbusStore = useModbusStore();
const { fetchSensorNames } = useSensorNames();
const { fetchSensorUnits } = useSensorUnits();
const { colsKey, rowsKey, timespanKey, intervalTimeMsKey } =
  useStorage("charts_page");

const items = [1, 2, 3];

const tab = ref();
const isPollingMode = ref<boolean>(modbusStore.isPollingMode);
const isDemoMode = ref<boolean>(modbusStore.isDemoMode);
const cols = ref(1);
const rows = ref(1);

const timespan = ref(10);
const timespanError = ref(false);

const intervalTimeMs = ref(100);

const modbusReqTimer = ref<TimerId>(0);

const rail = ref(false);

const navigateIcon = computed(() =>
  rail.value ? "navigate_before" : "navigate_next",
);

const chartCol = computed<number>(() => 12 / cols.value);

const timespanErrorMsg = computed(() =>
  timespanError.value ? `0 < VH <= ${modbusStore.maxTime}` : "",
);

const intervalLowLimitMs = computed<number>(() =>
  [AccessLevel.manufacturer, AccessLevel.full].includes(modbusStore.accessLevel)
    ? 10
    : 100,
);

const intervalTimeMsError = computed<boolean>(
  () =>
    intervalTimeMs.value < intervalLowLimitMs.value || !intervalTimeMs.value,
);

const intervalTimeMsErrorMsg = computed<string>(() =>
  intervalTimeMsError.value
    ? `Must be at least ${intervalLowLimitMs.value} ms.`
    : "",
);

const disabled = computed(
  () => modbusStore.portState === PortState.disconnected,
);

function handleNewIntervalTime(newIntervalTime: number) {
  // If the new interval time is too small, then reset it. The watcher will
  // send us back here.
  if (intervalTimeMsError.value) {
    intervalTimeMs.value = intervalLowLimitMs.value;
    return;
  }
  localStorage.setItem(intervalTimeMsKey.value, newIntervalTime.toString());
  if (isDemoMode.value) {
    clearInterval(modbusStore.demoTimer);
    modbusStore.demoTimer = setInterval(generateRandomData, newIntervalTime);
  }
  if (isPollingMode.value) {
    clearInterval(modbusStore.pollingTimer);
    modbusStore.pollingTimer = setInterval(pollingData, newIntervalTime);
  }
}

function pollingData() {
  modbusStore.pollingData(modbusStore.deviceAddress);
}

function generateRandomData() {
  modbusStore.generateRandomData();
}

function onKeyDown(e: KeyboardEvent): void {
  if (e.key.toLocaleLowerCase() == "." || e.key.toLocaleLowerCase() == "-") {
    e.preventDefault();
  }
}

function getViewSettingsFromLocalStorage() {
  // ---- rows
  if (!(rowsKey.value in localStorage)) {
    localStorage.setItem(rowsKey.value, "1");
  }
  rows.value = parseInt(localStorage.getItem(rowsKey.value) ?? "1");

  // ---- columns
  if (!(colsKey.value in localStorage)) {
    localStorage.setItem(colsKey.value, "1");
  }
  cols.value = parseInt(localStorage.getItem(colsKey.value) ?? "1");

  // ---- visible history
  if (!(timespanKey.value in localStorage)) {
    localStorage.setItem(timespanKey.value, "10");
  }
  timespan.value = parseInt(localStorage.getItem(timespanKey.value) ?? "10");

  // ---- refresh interval
  if (!(intervalTimeMsKey.value in localStorage)) {
    localStorage.setItem(intervalTimeMsKey.value, "100");
  }
  intervalTimeMs.value = parseInt(
    localStorage.getItem(intervalTimeMsKey.value) ?? "100",
  );
}

watch(
  () => modbusStore.portState,
  (newPortState: PortState) => {
    if (newPortState === PortState.disconnected) {
      isPollingMode.value = false;
    } else if (newPortState === PortState.connected) {
      isDemoMode.value = false;
      isPollingMode.value = true;
    }
    getViewSettingsFromLocalStorage();
  },
);

watch(
  isPollingMode,
  (newVal: boolean) => {
    modbusStore.isPollingMode = newVal;
    clearInterval(modbusStore.pollingTimer);
    if (newVal) {
      modbusStore.pollingTimer = setInterval(pollingData, intervalTimeMs.value);
    } else {
      modbusStore.pollingTimer = 0;
    }
  },
  {
    immediate: true,
  },
);

watch(
  isDemoMode,
  (newVal: boolean) => {
    modbusStore.isDemoMode = newVal;
    clearInterval(modbusStore.demoTimer);
    if (newVal) {
      modbusStore.demoTimer = setInterval(
        generateRandomData,
        intervalTimeMs.value,
      );
    } else {
      modbusStore.demoTimer = 0;
    }
  },
  {
    immediate: false,
  },
);

watch(cols, (newVal: number) => {
  localStorage.setItem(colsKey.value, newVal.toString());
});

watch(rows, (newVal: number) => {
  localStorage.setItem(rowsKey.value, newVal.toString());
});

watch(timespan, (newVal: number) => {
  if (newVal <= 0 || !newVal || newVal > modbusStore.maxTime) {
    timespanError.value = true;
  } else {
    timespanError.value = false;
    localStorage.setItem(timespanKey.value, newVal.toString());
  }
});

watch(
  () => modbusStore.accessLevel,
  () => {
    handleNewIntervalTime(intervalTimeMs.value);
  },
);

watch(intervalTimeMs, (newVal: number) => {
  handleNewIntervalTime(newVal);
});

onMounted(() => {
  modbusReqTimer.value = setInterval(async () => {
    if (
      !modbusStore.storeInit ||
      modbusStore.portState !== PortState.connected
    ) {
      return;
    }

    //-------- Init Chart Metadata -------------------------
    fetchSensorUnits(modbusStore.deviceAddress);
    fetchSensorNames(modbusStore.deviceAddress);

    clearInterval(modbusReqTimer.value);
  }, 200);

  isPollingMode.value = modbusStore.isPollingMode;
  isDemoMode.value = modbusStore.isDemoMode;

  getViewSettingsFromLocalStorage();
});

onUnmounted(() => {
  if (modbusReqTimer.value) {
    clearInterval(modbusReqTimer.value);
  }
});
</script>

<style scoped lang="scss">
.chart-action-btn:not(:has(.v-btn--disabled)):hover {
  background-color: #f2f2f2;
}
</style>
